import { useEffect, useState } from "react";
import { Modal, Button, Badge } from "react-bootstrap";
import { fetchWithAuth } from "../api/fetchWithAuth";

function SystemRequirementModal({ show, onClose, requirement }) {
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const getRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
  });

  const renderValue = (value) => value || <span className="text-muted">-</span>;
  const fieldLabels = {
    number: "Number",
    name: "Name",
    category: "Category",
    sil: "SIL",
    note: "Note",
    userId: "User",
    swRequirements: "SW Requirements",
    from: "From",
    to: "To"
  };

  useEffect(() => {
    if (!show || !requirement?.id || !showHistory) return;

    let cancelled = false;

    const loadHistory = async () => {
      setLoadingHistory(true);

      try {
        const res = await fetchWithAuth(`/api/system-requirements/${requirement.id}/history`);

        if (!res.ok) {
          if (!cancelled) {
            setHistory([]);
          }
          return;
        }

        const data = await res.json();

        if (!cancelled) {
          setHistory(data);
        }
      } catch (err) {
        if (!cancelled) {
          setHistory([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingHistory(false);
        }
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [show, requirement, showHistory]);

  useEffect(() => {
    if (!show) {
      setShowHistory(false);
      setHistory([]);
      setLoadingHistory(false);
    }
  }, [show]);

  const getActionBadge = (action) => {
    if (action === "create" || action === "move_from_requirement") {
      return <Badge bg="success">{action === "create" ? "Create" : "Move"}</Badge>;
    }

    if (action === "update" || action === "link_sw_requirements") {
      return <Badge bg="primary">{action === "update" ? "Update" : "Link"}</Badge>;
    }

    if (action === "delete") {
      return <Badge bg="danger">Delete</Badge>;
    }

    return <Badge bg="secondary">{action}</Badge>;
  };

  const formatChangeValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    if (Array.isArray(value)) {
      if (!value.length) return "-";

      return value.map((item) => {
        if (item && typeof item === "object") {
          return item.number ?? item.name ?? item.id ?? JSON.stringify(item);
        }

        return String(item);
      }).join(", ");
    }

    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    return String(value);
  };

  const renderHistoryChanges = (changes) => {
    const entries = Object.entries(changes ?? {});

    if (!entries.length) {
      return <span className="text-muted">-</span>;
    }

    return entries.map(([field, change]) => {
      const isChangePair = (
        change !== null &&
        typeof change === "object" &&
        !Array.isArray(change) &&
        (Object.prototype.hasOwnProperty.call(change, "old") || Object.prototype.hasOwnProperty.call(change, "new"))
      );

      return (
        <div key={field}>
          <strong>{fieldLabels[field] ?? field}</strong>: {isChangePair ? (
            <>
              {formatChangeValue(change.old)} {"->"} {formatChangeValue(change.new)}
            </>
          ) : (
            formatChangeValue(change)
          )}
        </div>
      );
    });
  };

  const renderSwRequirements = () => {
    const swRequirements = requirement.SWRequirements ?? [];

    if (!swRequirements.length) return <span className="text-muted">-</span>;

    return (
      <div className="d-flex flex-column gap-1">
        {swRequirements.map((swRequirement) => (
          <div key={swRequirement.id}>
            <span className="fw-semibold">{swRequirement.number}</span>
            <span className="text-muted"> - {swRequirement.name}</span>
          </div>
        ))}
      </div>
    );
  };

  if (!requirement) return null;

  return (
    <Modal show={show} onHide={onClose} size={showHistory ? "xl" : "lg"}>
      <Modal.Header closeButton>
        <Modal.Title>
          System Requirement {requirement.number}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <table className="table">
          <tbody>
            <tr style={getRowStyle(0)}>
              <th style={{ width: "140px" }}>Number</th>
              <td>{renderValue(requirement.number)}</td>
            </tr>
            <tr style={getRowStyle(1)}>
              <th>Name</th>
              <td style={{ whiteSpace: "pre-wrap" }}>{renderValue(requirement.name)}</td>
            </tr>
            <tr style={getRowStyle(2)}>
              <th>Category</th>
              <td>{renderValue(requirement.category)}</td>
            </tr>
            <tr style={getRowStyle(3)}>
              <th>SIL</th>
              <td>{renderValue(requirement.sil ?? requirement.SIL)}</td>
            </tr>
            <tr style={getRowStyle(4)}>
              <th>Note</th>
              <td style={{ whiteSpace: "pre-wrap" }}>{renderValue(requirement.note)}</td>
            </tr>
            <tr style={getRowStyle(5)}>
              <th>SW Requirements</th>
              <td>{renderSwRequirements()}</td>
            </tr>
            <tr style={getRowStyle(6)}>
              <th>User</th>
              <td>{renderValue(requirement.User?.name)}</td>
            </tr>
          </tbody>
        </table>

        <div className="d-flex justify-content-end">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowHistory((current) => !current)}
          >
            {showHistory ? "Hide history" : "Show history"}
          </Button>
        </div>

        {showHistory && (
          <div className="mt-4">
            <h6 className="mb-3">History</h6>

            {loadingHistory ? (
              <div>Loading...</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0" style={{ fontSize: "0.92rem" }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "180px" }}>When</th>
                      <th style={{ width: "120px" }}>Who</th>
                      <th style={{ width: "90px" }}>Action</th>
                      <th>Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-3">
                          No history found.
                        </td>
                      </tr>
                    ) : history.map((entry, index) => (
                      <tr key={entry.id} style={getRowStyle(index)}>
                        <td>{new Date(entry.createdAt).toLocaleString()}</td>
                        <td>{entry.User?.name ?? "-"}</td>
                        <td>{getActionBadge(entry.action)}</td>
                        <td>{renderHistoryChanges(entry.changes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default SystemRequirementModal;
